import * as React from 'react';

import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import {
  Button,
  DatePicker,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  WeightField,
  type Weight,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';

import { useTranslations } from '../../../i18n/useTranslations';

import { handleActionableError } from '~/modules/trade/utils/errorHandling';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface BrokeredLoadMarkAsShippedDialogProps {
  onMarkAsShipped: (
    shippedDate: CalendarDate | undefined,
    lightWeight: Weight | null,
    heavyWeight: Weight | null,
  ) => Promise<void>;
  onCancel?: () => void;
  shippedDate?: CalendarDate | undefined;
}

export const BrokeredLoadMarkAsShippedDialog: React.FC<
  BrokeredLoadMarkAsShippedDialogProps
> = ({ onMarkAsShipped, onCancel, shippedDate: initialShippedDate }) => {
  const t = useTranslations();
  const toast = useToast();
  const [, navigate] = useModuleLocation();
  const [shippedDate, setShippedDate] = React.useState<DateValue | null>(
    (initialShippedDate as unknown as DateValue) ?? today(getLocalTimeZone()),
  );
  const [lightWeight, setLightWeight] = React.useState<Weight | null>(null);
  const [heavyWeight, setHeavyWeight] = React.useState<Weight | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onMarkAsShipped(
        (shippedDate as unknown as CalendarDate) ?? undefined,
        lightWeight,
        heavyWeight,
      );
    } catch (error) {
      handleActionableError(error, toast, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = shippedDate;

  return (
    <DialogOverlay isOpen className="@BrokeredLoadMarkAsShippedDialog">
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">
            {t.brokerage.loads.dialogs.shipped.title}
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-4">
          <DatePicker
            value={shippedDate}
            onChange={setShippedDate}
            className="w-full"
            label={t.brokerage.loads.dialogs.shipped.shippedDate}
            isRequired={true}
            isDisabled={false}
          />
          <WeightField
            weightValue={lightWeight}
            onWeightValueChange={setLightWeight}
            defaultUnit="Pounds"
            unitLocked
            className="w-full"
            label={t.brokerage.loads.dialogs.shipped.lightWeight}
            isRequired={true}
            isDisabled={false}
          />
          <WeightField
            weightValue={heavyWeight}
            onWeightValueChange={setHeavyWeight}
            defaultUnit="Pounds"
            unitLocked
            className="w-full"
            label={t.brokerage.loads.dialogs.shipped.heavyWeight}
            isRequired={true}
            isDisabled={false}
          />
          <div className="flex justify-end gap-2 py-4">
            <Button variant="text" color="neutral" onPress={onCancel}>
              {t.brokerage.loads.dialogs.common.cancel}
            </Button>
            <Button
              variant="solid"
              onPress={handleConfirm}
              isLoading={isLoading}
              isDisabled={!isFormValid}
            >
              {t.brokerage.loads.dialogs.shipped.confirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
